import React, { useEffect } from 'react';
import { ArrowLeft, ExternalLink, Link2, Shield, BookOpen, Building2, GraduationCap } from 'lucide-react';
import { useApp } from './AppContext';

interface LinksPageProps {
    onBack: () => void;
}

// Link categories data

interface LinkItem {
    title: string;
    description: string;
    url: string;
    icon: React.ReactNode;
}

interface LinkCategory {
    title: string;
    icon: React.ReactNode;
    links: LinkItem[];
}

const LINK_CATEGORIES: LinkCategory[] = [
    {
        title: 'Официальные ресурсы',
        icon: <Building2 className="h-5 w-5" />,
        links: [
            {
                title: 'Росгвардия',
                description: 'Официальный сайт Федеральной службы войск национальной гвардии РФ',
                url: 'https://rosguard.gov.ru/',
                icon: <Shield className="h-5 w-5" />
            },
            {
                title: 'Консультант Плюс',
                description: 'Правовая база данных — законы и нормативные акты',
                url: 'https://www.consultant.ru/',
                icon: <BookOpen className="h-5 w-5" />
            },
            {
                title: 'Гарант',
                description: 'Информационно-правовой портал',
                url: 'https://www.garant.ru/',
                icon: <BookOpen className="h-5 w-5" />
            }
        ]
    },
    {
        title: 'Нормативные документы',
        icon: <BookOpen className="h-5 w-5" />,
        links: [
            {
                title: 'ФЗ-77 «О ведомственной охране»',
                description: 'Полный текст закона на Консультант Плюс',
                url: 'https://www.consultant.ru/document/cons_doc_LAW_22717/',
                icon: <Link2 className="h-5 w-5" />
            },
            {
                title: 'ФЗ «Об оружии»',
                description: 'Федеральный закон №150-ФЗ',
                url: 'https://www.consultant.ru/document/cons_doc_LAW_12679/',
                icon: <Link2 className="h-5 w-5" />
            },
            {
                title: 'Приказ Росгвардии №178',
                description: 'Порядок проведения периодических проверок (действует с 01.09.2026)',
                url: 'https://rosguard.gov.ru/ru/documents',
                icon: <Link2 className="h-5 w-5" />
            }
        ]
    },
    {
        title: 'Обучение и подготовка',
        icon: <GraduationCap className="h-5 w-5" />,
        links: [
            {
                title: 'Учебные центры Росгвардии',
                description: 'Подготовка и повышение квалификации охранников',
                url: 'https://rosguard.gov.ru/',
                icon: <GraduationCap className="h-5 w-5" />
            }
        ]
    }
];

export const LinksPage: React.FC<LinksPageProps> = ({ onBack }) => {
    const { getBackgroundClass } = useApp();

    // Update SEO meta tags
    useEffect(() => {
        // Schema.org structured data
        const schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.id = 'links-page-schema';
        schemaScript.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Полезные ссылки для охранников ВОХР",
            "description": "Подборка полезных ссылок для ведомственной охраны: Росгвардия, порталы трудоустройства, учебные центры, нормативные документы.",
            "url": "https://vohr-test.ru/links"
        });

        const existingSchema = document.getElementById('links-page-schema');
        if (existingSchema) existingSchema.remove();
        document.head.appendChild(schemaScript);

        return () => {
            const schema = document.getElementById('links-page-schema');
            if (schema) schema.remove();
        };
    }, []);

    const handleLinkClick = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
            {/* Header */}
            <header className="app-header-fixed flex items-center">
                <button
                    onClick={onBack}
                    className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
                    aria-label="Назад"
                >
                    <ArrowLeft className="arrow-icon" />
                </button>
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <Link2 className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
                    <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold glass-text truncate">
                        Полезные ссылки
                    </h1>
                </div>
            </header>

            {/* Main Content */}
            <main className="app-content-scrollable">
                {/* Intro */}
                <section className="mb-6">
                    <p className="glass-text-secondary text-sm sm:text-base">
                        Подборка официальных ресурсов и полезных материалов для работников ведомственной охраны.
                    </p>
                </section>

                {/* Link Categories */}
                {LINK_CATEGORIES.map((category, idx) => (
                    <section key={idx} className="mb-8">
                        <h2 className="text-base sm:text-lg font-semibold glass-text mb-4 flex items-center gap-2">
                            <span className="glass-icon">{category.icon}</span>
                            {category.title}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {category.links.map((link, linkIdx) => (
                                <article
                                    key={linkIdx}
                                    className="glass-panel rounded-xl glass-interactive p-4 sm:p-5 cursor-pointer"
                                    onClick={() => handleLinkClick(link.url)}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="glass-icon flex-shrink-0 p-2 bg-white/10 rounded-lg">
                                            {link.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold glass-text text-sm sm:text-base mb-1 flex items-center gap-2">
                                                {link.title}
                                                <ExternalLink className="h-3 w-3 opacity-60" />
                                            </h3>
                                            <p className="glass-text-secondary text-xs sm:text-sm line-clamp-2">
                                                {link.description}
                                            </p>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>
                ))}

            </main>
        </div>
    );
};
